import { updateQuoteMarkers } from "@stryker/core/api/quotes";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { QuoteListItem, UpdateQuoteMarkersDto } from "@stryker/core/types/quotes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, isAfter } from "date-fns";
import { useNavigate } from "react-router-dom";
import Status from "@/components/job/Status";
import { Button } from "@/components/ui/button";
import DatePicker from "@/components/ui/date-picker";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Typography } from "@/components/ui/typography";
import { useConfigOptions } from "@/hooks/useConfigOptions";

type QuoteCardProps = {
  quote: QuoteListItem;
};

export default function QuoteCard({ quote }: QuoteCardProps) {
  const navigate = useNavigate();

  const goToDetails = () => {
    if (quote.quote) {
      navigate(`/quotes/${quote.quote.id}?job_number=${quote.job_number}&repair_system=${quote.repair_system}`);
    }
  };

  const queryClient = useQueryClient();
  const { mutate: updateQuoteMarkersMutate } = useMutation({
    mutationFn: (payload: { id: number; data: UpdateQuoteMarkersDto }) => updateQuoteMarkers(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.QuotesList],
      });
    },
  });

  const { jobFlagsOptions } = useConfigOptions();

  return (
    <div onClick={goToDetails} className="rounded-xl border border-solid border-gray-200 px-4 py-5">
      <div className="mb-4 flex items-center gap-1">
        <span className="flex-2 max-w-[57%] text-ellipsis overflow-clip">{quote.job_number}</span>
        <Typography className="flex-1">Date Run</Typography>
        <Typography className="flex-1">
          {quote.date_run ? format(new Date(quote.date_run), "MM.dd.yyyy") : "N/A"}
        </Typography>
        <Status status={quote.quote?.quote_status} className="ml-auto" />
      </div>
      <div className="mt-2 flex justify-between gap-4">
        <div className="flex-1">
          <Typography className="text-gray-500">Plant Name</Typography>
          <Typography className="text-gray-800" variant="text-md" weight="semibold">
            {quote.plant_name}
          </Typography>
        </div>
        <div className="flex-1">
          <Typography className="text-gray-500">Total revenue:</Typography>
          <Typography className="text-gray-800" variant="text-md" weight="semibold">
            {quote.quote?.total_revenue ?? "N/A"}
          </Typography>
        </div>
      </div>
      {quote.quote?.id && (
        <div className="my-4 flex justify-between gap-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex-1">
            <Typography className="text-gray-500">Bid Status:</Typography>
            <Select
              value={quote.quote?.bid_status ?? undefined}
              onValueChange={(value) => {
                updateQuoteMarkersMutate({ id: quote.quote!.id, data: { bid_status: Number(value) } });
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select bid status" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {jobFlagsOptions?.map(({ value, title }) => {
                    return (
                      <SelectItem key={value} value={String(value)}>
                        {title}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 ">
            <Typography className="text-gray-500">Bid Status Date:</Typography>
            <DatePicker
              date={quote.quote.bid_status_date ? new Date(quote.quote.bid_status_date) : undefined}
              onChange={(date) => {
                updateQuoteMarkersMutate({
                  id: quote.quote!.id,
                  data: { bid_status_date: format(date, "yyyy-MM-dd") },
                });
              }}
              disabled={(date) => isAfter(date, new Date())}
            />
          </div>
        </div>
      )}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          const url = quote.quote?.id
            ? `/quotes/create?job_number=${quote.job_number}&quote_id=${quote.quote.id}&repair_system=${quote.repair_system}`
            : `/quotes/create?job_number=${quote.job_number}&repair_system=${quote.repair_system}`;
          navigate(url);
        }}
        className="w-full mb-2 mt-4"
      >
        {quote.quote?.id ? "Edit" : "Add"}
      </Button>
    </div>
  );
}
